import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi, type ValueList, type ValueListItem } from '../../services/settings';
import { TableWrapper, TableHead, FilterRow } from '../../components/TableWrapper';

export function ValueListsPage() {
  const [lists, setLists] = useState<ValueList[]>([]);
  const [selectedListKey, setSelectedListKey] = useState<string | null>(null);
  const [items, setItems] = useState<ValueListItem[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const { error, success, info } = useToast();

  const loadLists = useCallback(async () => {
    try {
      const res = await settingsApi.getValueLists();
      setLists(res.valueLists);
      if (!selectedListKey && res.valueLists.length > 0) {
        setSelectedListKey(res.valueLists[0].listKey);
      }
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta listor');
    } finally {
      setLoading(false);
    }
  }, [error, selectedListKey]);

  const loadItems = useCallback(async () => {
    if (!selectedListKey) return;
    try {
      const res = await settingsApi.getValueListItems(selectedListKey);
      setItems(res.items);
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta värden');
    }
  }, [error, selectedListKey]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (selectedListKey) loadItems();
  }, [selectedListKey, loadItems]);

  const handleAdd = async () => {
    if (!selectedListKey || !newLabel.trim()) {
      info('Ange ett etikettvärde');
      return;
    }
    try {
      await settingsApi.createValueListItem(selectedListKey, { label: newLabel.trim() });
      success('Värde tillagt');
      setNewLabel('');
      loadItems();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte lägga till');
    }
  };

  const handleToggleHidden = async (item: ValueListItem) => {
    try {
      await settingsApi.updateValueListItem(item.id, { isHidden: item.isHidden ? false : true });
      success(item.isHidden ? 'Värde visat' : 'Värde dolt');
      loadItems();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte uppdatera');
    }
  };

  const handleDelete = async (item: ValueListItem) => {
    if (item.isSystemSeed) {
      info('Systemvärden kan inte raderas; dölj i stället.');
      return;
    }
    try {
      await settingsApi.deleteValueListItem(item.id);
      success('Värde borttaget');
      loadItems();
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte radera');
    }
  };

  const filteredItems = items.filter(
    (i) => !filter || i.label.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  const currentList = lists.find((l) => l.listKey === selectedListKey);

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Värdelistor</h2>
      <div className="flex flex-wrap gap-2">
        {lists.map((l) => (
          <button
            key={l.listKey}
            type="button"
            onClick={() => setSelectedListKey(l.listKey)}
            className={`px-2 py-1 rounded text-sm ${
              selectedListKey === l.listKey
                ? 'bg-primary text-white'
                : 'border border-gray-300 hover:bg-gray-100'
            }`}
          >
            {l.name}
          </button>
        ))}
      </div>
      {currentList && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Filtrera värden"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-48"
            />
            <WithWrite moduleCode="settings">
              <input
                type="text"
                placeholder="Nytt värde"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                className="w-40"
              />
              <button
                type="button"
                onClick={handleAdd}
                className="px-2 py-1 text-sm bg-success text-white rounded hover:bg-success-hover"
              >
                Lägg till
              </button>
            </WithWrite>
          </div>
          <TableWrapper>
            <TableHead>
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">Etikett</th>
                <th className="text-left px-2 py-1.5 font-medium">Sortering</th>
                <th className="text-left px-2 py-1.5 font-medium">Status</th>
                <WithWrite moduleCode="settings">
                  <th className="text-left px-2 py-1.5 font-medium">Åtgärder</th>
                </WithWrite>
              </tr>
              <FilterRow>
                <td colSpan={4} className="px-2 py-1" />
              </FilterRow>
            </TableHead>
            <tbody>
              {filteredItems.map((i) => (
                <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1">{i.label}</td>
                  <td className="px-2 py-1">{i.sortOrder}</td>
                  <td className="px-2 py-1">
                    {i.isHidden ? <span className="text-gray-400">Dold</span> : 'Synlig'}
                  </td>
                  <WithWrite moduleCode="settings">
                    <td className="px-2 py-1">
                      <button
                        type="button"
                        onClick={() => handleToggleHidden(i)}
                        className="text-sm text-gray-600 hover:underline mr-2"
                      >
                        {i.isHidden ? 'Visa' : 'Dölj'}
                      </button>
                      {!i.isSystemSeed && (
                        <button
                          type="button"
                          onClick={() => handleDelete(i)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Radera
                        </button>
                      )}
                    </td>
                  </WithWrite>
                </tr>
              ))}
            </tbody>
          </TableWrapper>
          {filteredItems.length === 0 && (
            <p className="text-sm text-gray-500">Inga värden eller inga träffar på filtret.</p>
          )}
        </>
      )}
    </div>
  );
}

